import type { RoomOccupancy } from '@/types/SearchBar';

/**
 * Passengers and room split in the shape the Player SOAP builders consume.
 * GetProductOffers and GetFilters must send exactly the same occupancy, because
 * the hotel's dates and lengths depend on both the ages and the room split.
 */
export interface PlayerOccupancy {
  /** Adult count, never below 1 (the builder's single-room fallback needs one). */
  adults: number;
  children: Array<{ age: number }>;
  passengers: Array<{ age: number; type: 'adult' | 'child' }>;
  /** Passenger ids 1..n in room order. */
  rooms: Array<{ passengerIds: number[] }>;
}

const ADULT_AGE = 35;
const DEFAULT_CHILD_AGE = 10;

/**
 * Maps the UI occupancy to Player passengers: adults are sent as 35, children
 * with their age (10 when unknown), ids numbered across rooms in order.
 */
export function occupancyToPlayerPax(occupancy: RoomOccupancy): PlayerOccupancy {
  const passengers: PlayerOccupancy['passengers'] = [];
  const rooms: PlayerOccupancy['rooms'] = [];

  let passengerId = 1;

  occupancy.rooms.forEach((room) => {
    const passengerIds: number[] = [];

    room.persons.forEach((person) => {
      const age = person.type === 'adult' ? ADULT_AGE : (person.age ?? DEFAULT_CHILD_AGE);
      passengers.push({ age, type: person.type });
      passengerIds.push(passengerId);
      passengerId++;
    });

    rooms.push({ passengerIds });
  });

  return {
    adults: passengers.filter((p) => p.type === 'adult').length || 1,
    children: passengers.filter((p) => p.type === 'child').map((p) => ({ age: p.age })),
    passengers,
    rooms,
  };
}
